import { useEffect, useState, useCallback } from 'react';
import Navbar from '../components/common/Navbar';
import { useAuth } from '../context/AuthContext';

const PRODIGY_URL = import.meta.env.VITE_PRODIGY_URL || (import.meta.env.DEV ? 'http://localhost:8085' : '/prodigy');
const REFRESH_INTERVAL = 60;

const TYPE_META = {
  pos:       { label: 'POS Tagging',  color: '#2563eb', barColor: 'bg-blue-500',   badge: 'bg-blue-100 text-blue-700'   },
  features:  { label: 'Morfología',   color: '#059669', barColor: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  relations: { label: 'Relaciones',   color: '#d97706', barColor: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-700'  },
  ner:       { label: 'NER',          color: '#7c3aed', barColor: 'bg-violet-500', badge: 'bg-violet-100 text-violet-700' },
};

const ProgressBar = ({ pct, barColor }) => (
  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
    <div
      className={`h-2 rounded-full transition-all duration-500 ${barColor || 'bg-blue-500'}`}
      style={{ width: `${pct}%` }}
    />
  </div>
);

const UserRow = ({ name, done, total, complete, unassigned, barColor }) => {
  const pct = Math.min(100, Math.round((done / total) * 100));
  return (
    <div className={`flex items-center gap-3 py-1 ${unassigned ? 'opacity-40' : ''}`}>
      <span className="w-4 text-emerald-600 text-sm font-bold">{complete ? '✓' : ''}</span>
      <span className="w-32 text-sm text-gray-700 truncate" title={name}>{name}</span>
      <div className="flex-1">
        <ProgressBar pct={pct} barColor={complete ? 'bg-emerald-500' : barColor} />
      </div>
      <span className="text-xs text-gray-500 w-14 text-right">{done}/{total}</span>
    </div>
  );
};

const TypeCard = ({ atype, info, sentencesPerBatch }) => {
  const meta = TYPE_META[atype] || { label: atype, color: '#64748b', barColor: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600' };
  const pct = info.validators_assigned > 0
    ? Math.round((info.validators_complete / info.validators_assigned) * 100)
    : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <span className="font-semibold uppercase tracking-wide text-sm" style={{ color: meta.color }}>
          {meta.label}
        </span>
        <span className={`text-xs font-bold px-3 py-1 rounded-full ${meta.badge}`}>
          Batch {String(info.batch).padStart(2, '0')}
        </span>
      </div>

      {/* Summary numbers */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { num: info.validators_complete, label: 'Completaron' },
          { num: info.validators_assigned, label: 'Asignados' },
          { num: `${pct}%`, label: 'Progreso' },
        ].map(({ num, label }) => (
          <div key={label} className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
            <div className="text-xl font-bold" style={{ color: meta.color }}>{num}</div>
            <div className="text-xs text-gray-400 uppercase mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Global bar */}
      <div className="mb-4">
        <ProgressBar pct={pct} barColor={meta.barColor} />
      </div>

      {/* Per-user rows */}
      <div className="space-y-1">
        {Object.entries(info.per_user).map(([name, u]) => (
          <UserRow
            key={name}
            name={name}
            done={u.done}
            total={sentencesPerBatch}
            complete={u.complete}
            unassigned={u.unassigned}
            barColor={meta.barColor}
          />
        ))}
      </div>
    </div>
  );
};

const ReportPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);

  // Daily tab state
  const [activeTab, setActiveTab] = useState('progress');
  const [dailyDate, setDailyDate] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  const fetchReport = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`${PRODIGY_URL}/api/full-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (e) {
      setError(`No se pudo cargar el reporte: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDailyReport = useCallback(async (date) => {
    const token = localStorage.getItem('access_token');
    setDailyLoading(true);
    setDailyError(null);
    try {
      // date puede ser null → muestra todos los datasets de sesión
      const url = date
        ? `${PRODIGY_URL}/api/daily-report?date=${date}`
        : `${PRODIGY_URL}/api/daily-report`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setDailyData(json);
      if (json.error) setDailyError(json.error);
    } catch (e) {
      setDailyError(`No se pudo cargar: ${e.message}`);
    } finally {
      setDailyLoading(false);
    }
  }, []);

  // Progress tab: initial load + auto-refresh
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { fetchReport(); return REFRESH_INTERVAL; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [fetchReport]);

  // Daily tab: load when tab opens or date filter changes
  useEffect(() => {
    if (activeTab === 'daily') fetchDailyReport(dailyDate);
  }, [activeTab, dailyDate, fetchDailyReport]);

  const handleRefresh = () => { setLoading(true); setCountdown(REFRESH_INTERVAL); fetchReport(); };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reporte de Anotaciones</h1>
            <p className="text-gray-500 text-sm mt-1">Progreso y actividad del equipo de anotadores</p>
          </div>
          {activeTab === 'progress' && (
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">Actualiza en {countdown}s</span>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition"
              >
                {loading ? 'Cargando...' : '↻ Actualizar'}
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
          {[
            { id: 'progress', label: 'Progreso General' },
            { id: 'daily',    label: 'Actividad Diaria' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB: PROGRESO GENERAL ── */}
        {activeTab === 'progress' && (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm">{error}</div>
            )}
            {loading && !data && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[1,2,3,4].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-64 shadow-sm" />
                ))}
              </div>
            )}
            {data && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {Object.entries(data.report).map(([atype, info]) => {
                    const meta = TYPE_META[atype] || { label: atype, color: '#64748b' };
                    const pct = info.validators_assigned > 0
                      ? Math.round((info.validators_complete / info.validators_assigned) * 100) : 0;
                    return (
                      <div key={atype} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <div className="text-xs uppercase tracking-wide font-semibold mb-1" style={{ color: meta.color }}>{meta.label}</div>
                        <div className="text-2xl font-bold text-gray-900">{pct}%</div>
                        <div className="text-xs text-gray-400 mt-1">{info.validators_complete}/{info.validators_assigned} listos</div>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {Object.entries(data.report).map(([atype, info]) => (
                    <TypeCard key={atype} atype={atype} info={info} sentencesPerBatch={data.sentences_per_batch} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── TAB: ACTIVIDAD DIARIA ── */}
        {activeTab === 'daily' && (
          <>
            {/* Date filter + refresh */}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <label className="text-sm text-gray-600 font-medium">Filtrar por fecha inicio sesión:</label>
              <input
                type="date"
                max={today}
                value={dailyDate || ''}
                onChange={(e) => setDailyDate(e.target.value || null)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {dailyDate && (
                <button
                  onClick={() => setDailyDate(null)}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Limpiar filtro
                </button>
              )}
              <button
                onClick={() => fetchDailyReport(dailyDate)}
                disabled={dailyLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition"
              >
                {dailyLoading ? 'Cargando...' : '↻ Actualizar'}
              </button>
              {dailyData && !dailyError && (
                <span className="text-sm text-gray-500">
                  {dailyData.total_annotations} anotaciones · {dailyData.total_users} anotadores
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mb-6">
              El filtro de fecha corresponde a cuándo se inició la sesión de anotación (Prodigy no registra timestamp por oración individual).
            </p>

            {dailyError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm">{dailyError}</div>
            )}

            {dailyLoading && !dailyData && (
              <div className="space-y-3">
                {[1,2,3].map((i) => <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-20 shadow-sm" />)}
              </div>
            )}

            {dailyData && !dailyLoading && Object.keys(dailyData.by_user).length === 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
                <div className="text-4xl mb-3">📭</div>
                <p className="text-gray-500 text-sm">{dailyDate ? `Sin actividad registrada para el ${dailyDate}` : 'Sin datos de anotación disponibles'}</p>
              </div>
            )}

            {dailyData && Object.entries(dailyData.by_user).map(([username, udata]) => (
              <DailyUserCard
                key={username}
                username={username}
                udata={udata}
                expanded={expandedUser === username}
                onToggle={() => setExpandedUser(expandedUser === username ? null : username)}
              />
            ))}
          </>
        )}

      </div>
    </div>
  );
};

// ─── Daily Activity Card ───────────────────────────────────────────────────

const ANSWER_BADGE = {
  accept:  'bg-green-100 text-green-700',
  reject:  'bg-red-100 text-red-700',
  ignore:  'bg-gray-100 text-gray-500',
};

const DailyUserCard = ({ username, udata, expanded, onToggle }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-3 overflow-hidden">
      {/* Header row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm flex items-center justify-center uppercase">
            {username[0]}
          </div>
          <div>
            <div className="font-semibold text-gray-900">{username}</div>
            <div className="flex gap-2 mt-1">
              {Object.entries(udata.by_type).map(([t, cnt]) => {
                const meta = TYPE_META[t] || { label: t, badge: 'bg-gray-100 text-gray-600' };
                return (
                  <span key={t} className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.badge}`}>
                    {meta.label}: {cnt}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-lg font-bold text-gray-900">{udata.total}</div>
            <div className="text-xs text-gray-400">oraciones</div>
          </div>
          {/* Promedio de duración si hay timestamps precisos */}
          {udata.has_precise_timestamps && (() => {
            const durs = udata.annotations.filter(a => a.duration_ms != null).map(a => a.duration_ms);
            if (durs.length === 0) return null;
            const avg = durs.reduce((s, d) => s + d, 0) / durs.length;
            return (
              <div className="text-right hidden sm:block">
                <div className="text-sm font-semibold text-emerald-600">
                  {avg < 60000 ? `${(avg / 1000).toFixed(1)}s` : `${Math.floor(avg / 60000)}m${Math.round((avg % 60000) / 1000)}s`}
                </div>
                <div className="text-xs text-gray-400">prom/oración</div>
              </div>
            );
          })()}
          <span className="text-gray-400 text-lg">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {/* Expanded annotation list */}
      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-50 max-h-[480px] overflow-y-auto">
          {/* Header indicador de precisión */}
          {udata.annotations.length > 0 && (
            <div className="px-5 py-2 bg-gray-50 flex items-center gap-2">
              {udata.has_precise_timestamps ? (
                <span className="text-xs text-green-600 font-medium">Timestamps precisos disponibles</span>
              ) : (
                <span className="text-xs text-amber-500">Fecha estimada (nivel de sesión) — timestamps precisos disponibles en nuevas anotaciones</span>
              )}
            </div>
          )}
          {udata.annotations.map((ann, idx) => (
            <div key={idx} className="px-5 py-3 hover:bg-gray-50">
              <div className="flex items-start justify-between gap-3 mb-1">
                <p className="text-sm text-gray-800 flex-1 leading-relaxed">{ann.text || <span className="text-gray-400 italic">sin texto</span>}</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ANSWER_BADGE[ann.answer] || 'bg-gray-100 text-gray-500'}`}>
                    {ann.answer}
                  </span>
                  {ann.batch && (
                    <span className="text-xs text-gray-400">B{String(ann.batch).padStart(2,'0')}</span>
                  )}
                  {/* Timestamp: preciso (hora:min:seg) o estimado (solo fecha) */}
                  {ann.annotated_at && (
                    <span className={`text-xs ${ann.has_precise_ts ? 'text-blue-400 font-mono' : 'text-gray-300'}`} title={ann.has_precise_ts ? 'Hora exacta de anotación' : 'Fecha estimada (inicio de sesión)'}>
                      {ann.annotated_at}
                    </span>
                  )}
                  {/* Duración por oración */}
                  {ann.duration_ms != null && (
                    <span className="text-xs text-emerald-500 font-mono" title="Tiempo en anotar esta oración">
                      {ann.duration_ms < 60000
                        ? `${(ann.duration_ms / 1000).toFixed(1)}s`
                        : `${Math.floor(ann.duration_ms / 60000)}m${Math.round((ann.duration_ms % 60000) / 1000)}s`}
                    </span>
                  )}
                </div>
              </div>

              {/* Annotation details */}
              {ann.summary && (() => {
                const tagged  = ann.summary.tagged_tokens || [];
                const rels    = ann.summary.relations     || [];
                const TYPE_BADGE = {
                  pos:      'bg-blue-50 text-blue-700 border-blue-200',
                  features: 'bg-green-50 text-green-700 border-green-200',
                  ner:      'bg-violet-50 text-violet-700 border-violet-200',
                  relations:'bg-amber-50 text-amber-700 border-amber-200',
                };
                const badgeCls = TYPE_BADGE[ann.type] || 'bg-gray-50 text-gray-600 border-gray-200';
                return (
                  <div className="mt-2 space-y-1">
                    {/* POS / Features / NER: tokens con su etiqueta */}
                    {tagged.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {tagged.map((t, i) => (
                          <span key={i} className={`inline-flex items-center gap-1 text-xs border px-2 py-0.5 rounded-full ${badgeCls}`}>
                            <span className="font-medium">{t.text}</span>
                            <span className="opacity-60 text-[10px] uppercase tracking-wide">{t.tag}</span>
                          </span>
                        ))}
                        {(ann.summary.span_count > tagged.length) && (
                          <span className="text-xs text-gray-400 self-center">+{ann.summary.span_count - tagged.length} más</span>
                        )}
                      </div>
                    )}
                    {/* Sin etiquetas */}
                    {tagged.length === 0 && rels.length === 0 && ann.answer !== 'ignore' && (
                      <p className="text-xs text-gray-400 italic">Sin etiquetas asignadas</p>
                    )}
                    {/* Relations */}
                    {rels.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rels.map((r, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">
                            <span className="font-medium">{r.head}</span>
                            <span className="text-amber-400">→</span>
                            <span className="font-semibold text-[10px] uppercase tracking-wide">{r.label}</span>
                            <span className="text-amber-400">→</span>
                            <span className="font-medium">{r.child}</span>
                          </span>
                        ))}
                        {(ann.summary.relation_count > rels.length) && (
                          <span className="text-xs text-gray-400 self-center">+{ann.summary.relation_count - rels.length} más</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportPage;
